import { makeStyles } from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormControlProps,
  Input,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  TextField,
  Typography,
} from '@mui/material';

import { useAppStore } from '@features/common/store/AppStore';
import { useApi } from '@hooks/useApi';
import { createFilterOptions } from '@mui/material';
import { CalendarType, Product, ProductTenor } from '@protos/product';
import darkTheme from '@shared/themes/darkTheme';
import { capitalise } from '@utils/formatter';
import { checkStringIsNumeric } from '@utils/number';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SimplePriceTicker } from './PriceTicker';

export type ProductEntryActionFunction = (
  product: Product,
  tenor: ProductTenor | null,
  front: ProductTenor | null,
  back: ProductTenor | null,
  targetPrice: number,
  exchange: string | null
) => void;

export type ProductEntryAction = {
  label: string;
  action: ProductEntryActionFunction;
  closeOnSubmit: boolean;
};

export interface ProductEntryProps {
  onSubmit: ProductEntryActionFunction;
  onCancel: () => void;
  hideTargetPrice?: boolean;
  hidePrices?: boolean;
  hideExchange?: boolean;
  submitLabel?: string;
  restrictProductsToTradable?: boolean; // TODO USE THIS
  otherActions?: ProductEntryAction[];
}

export const productEntryContainerId = 'product-entry-container';

enum EProductEntrySection {
  None = 0,
  Search = 1,
  Add = 2,
}

const useStyleOverrideClasses = makeStyles(theme => {
  return {
    menuPaper: {
      maxHeight: '12em',
    },
  };
});

const actionButtonStyle = {
  transition: 'all 0.1s ease',
  lineHeight: '1em',
  height: '3em',
};

type VisibilityProps = Pick<ProductEntryProps, 'hideTargetPrice' | 'hidePrices' | 'hideExchange'>;

const visibilityDefaults: VisibilityProps = {
  hideTargetPrice: false,
  hidePrices: false,
  hideExchange: true,
};

const getSectionVisibility = (props: ProductEntryProps) => {
  return { ...visibilityDefaults, ...props };
};

export const ProductEntry = (props: ProductEntryProps) => {
  const { onSubmit, onCancel } = props;
  const classes = useStyleOverrideClasses(props);

  const { tenors, products: inProducts } = useAppStore();

  // Restrict tenors shown to a reasonable time period
  // TODO: check the cut-off period for yearly, daily, etc
  const tenorsBeforeCutoff: ProductTenor[] = useMemo(() => {
    const now = new Date().getTime();
    const oneMonth = 1000 * 60 * 60 * 24 * 30;

    return (
      tenors?.filter(t => {
        const tenorTime = new Date(t.tenor_date || 'NONE').getTime();
        switch (t.frequency) {
          case 'monthly':
          case 'quarterly':
            return tenorTime < now + oneMonth * 25;

          case 'yearly':
            return tenorTime < now + oneMonth * 12 * 5;

          default:
            return true;
        }
      }) || []
    );
  }, [tenors]);

  const { apiClient } = useApi();

  const { hideTargetPrice, hidePrices, hideExchange } = getSectionVisibility(props);

  const products = (inProducts || [])
    .slice()
    .sort((a, b) => {
      if (a.product_group == b.product_group) {
        return a.symbol < b.symbol ? -1 : 1;
      } else {
        return a.product_group < b.product_group ? -1 : 1;
      }
    })
    .filter(p => {
      return p.enabled;
    });

  const productGroups = useMemo(() => {
    return [...new Set(products.map(p => p.product_group))];
  }, [products]);

  // This does in fact just reduce to 'ice' and 'cme' at the moment...
  const exchanges = useMemo(() => {
    return [
      ...new Set(
        products
          .map(p => p.exchange_symbols)
          .flat()
          .map(e => e.exchange)
      ),
    ];
  }, [products]);

  const [group, setSelectedGroup] = useState<string | null>(null);

  const availableProducts = products.filter(p => {
    return p.product_group === group;
  });

  const [product, setProduct] = useState<Product | null>(null);
  const [tenor, setTenor] = useState<ProductTenor | null>(null);
  const [targetPrice, setTargetPrice] = useState<string | null>(null);

  // TODO: Currently unused, will be required when (or if) we start watching prices
  // for things that aren't monthly. At present these need the RFQ service, but there's
  // not currently enough data (or market activity) to
  const [frontTenor, setFrontTenor] = useState<ProductTenor | null>(null);
  const [backTenor, setBackTenor] = useState<ProductTenor | null>(null);
  const [exchange, setExchange] = useState<string | null>(null);

  // Mostly to be used with the above
  const getIsSpread = (product: Product | null) => {
    return product && product.calendar_type === CalendarType.SPREAD;
  };

  const isSpread = getIsSpread(product);

  // Search
  const [searchString, setSearchString] = useState<string | null>(null);

  // Tenors that can be used with the current product
  const validTenors = (tenorsBeforeCutoff || []).filter(t => t.frequency === product?.tenor_frequency) || [];

  /*
  
  Interactions & handlers
    
  */

  // === Interaction tracking ===
  // Keep track of the focussed section to perform the right action if / when they press 'enter'
  const focussedSection = useRef<EProductEntrySection>(EProductEntrySection.Search);

  // True if there's a valid tenor
  const hasValidTenor = tenor !== null;

  const actionButtonsDisabled =
    !hasValidTenor || product === null || (hideTargetPrice ? false : targetPrice === null) || (hideExchange ? false : exchange === null);

  const handleGroupChange = (e: SelectChangeEvent<string | null>) => {
    setSelectedGroup(e.target.value);
    setTenor(null);
    setProduct(null);
    setTargetPrice(null);
    setExchange(null);
  };

  const fetchTenorsForProduct = async (product: Product) => {
    return await apiClient?.getProductBySymbol(product.symbol);
  };

  const switchToProduct = (product: Product | null) => {
    setProduct(product);

    // Need to select a product before we can show tenors
    if (product === null) {
      setTenor(null);
      setTargetPrice(null);
      setExchange(null);
      return;
    } else if (isSpread !== getIsSpread(product)) {
      setTenor(null);
      setTargetPrice(null);
      setFrontTenor(null);
      setBackTenor(null);
    }

    // Clear the tenors if the frequency on the new product doesn't match
    if (!tenor || tenor.frequency !== product?.tenor_frequency) {
      setTenor(null);
      setFrontTenor(null);
      setBackTenor(null);
      setTargetPrice(null);
    }

    // If the exchange is unspecified they'll need to pick it; otherwise, use the default
    if (product.default_exchange === 'unspecified') {
      setExchange(null);
    } else {
      setExchange(product.default_exchange);
    }

    fetchTenorsForProduct(product);
  };

  const handleProductChange = (e: SelectChangeEvent<string | null>) => {
    const selectedProduct = products.find(p => p.symbol === e.target.value) || null;
    switchToProduct(selectedProduct);
  };

  const handleTenorChange = (e: SelectChangeEvent<string | null>) => {
    const selectedTenor = tenors?.find(t => t.code === e.target.value);
    setTenor(selectedTenor || null);
  };

  const handleTargetPriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (checkStringIsNumeric(e.target.value)) {
      setTargetPrice(e.target.value);
    }
  };

  const reset = () => {
    setTenor(null);
    setFrontTenor(null);
    setBackTenor(null);
    setProduct(null);
    setSelectedGroup(null);
    setTargetPrice(null);
    setSearchString(null);
    setExchange(null);
    focussedSection.current = EProductEntrySection.None;
  };

  const submit = () => {
    if (actionButtonsDisabled) return;
    onSubmit(product, tenor, frontTenor, backTenor, Number(targetPrice), exchange);
    reset();
  };

  const submitAction = (entryAction: ProductEntryAction) => {
    if (!product || !hasValidTenor || !targetPrice) return;
    entryAction.action(product, tenor, frontTenor, backTenor, Number(targetPrice), exchange);
  };

  const cancel = () => {
    reset();
    onCancel();
  };

  const registerFormInteraction = useCallback(() => {
    focussedSection.current = EProductEntrySection.Add;
  }, []);

  const registerSearchInteraction = useCallback(() => {
    focussedSection.current = EProductEntrySection.Search;
  }, []);

  // Handle keyboard submission
  useEffect(() => {
    const onKeyPress = (event: KeyboardEvent) => {
      if (event.code == 'Enter' || event.code == 'NumpadEnter') {
        if (focussedSection.current !== EProductEntrySection.Search) {
          submit();
          event.preventDefault();
        }
      }
    };
    document.addEventListener('keypress', onKeyPress);
    return () => {
      document.removeEventListener('keypress', onKeyPress);
    };
  }, [focussedSection, product, targetPrice, tenor]);

  // This is called when the user enters a new search term
  const onSelectionChange = (e: React.SyntheticEvent<Element, Event>, value: string | Product | null, reason: AutocompleteChangeReason) => {
    if (!value) {
      return;
    }

    // Not entirely clear what should happen here
    if (reason !== 'selectOption') {
      return;
    }

    if (typeof value === 'object') {
      setSelectedGroup(value.product_group);
      setSearchString('');
      switchToProduct(value);
    }

    if (typeof value === 'string') {
      const maybeValidProduct = products.find(p => p.symbol === searchString);
      if (maybeValidProduct) {
        setSelectedGroup(maybeValidProduct.product_group);
        switchToProduct(maybeValidProduct);
        setSearchString('');
      }
    }
  };

  // Search box string completion
  const onAutocompleteInputChange = (e: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'selectOption') {
      return;
    }
    setSearchString(value);
  };

  /*
  
    Widget content
    
    */

  const DefaultFormControlProps: Partial<FormControlProps> = {
    variant: 'standard',
    sx: { m: 1, minWidth: 120, flexGrow: 1 },
  };

  const DefaultSelectProps: Partial<SelectProps<string | null>> = {
    MenuProps: {
      classes: { paper: classes.menuPaper },
    },
    onClick: registerFormInteraction,
    onFocus: registerFormInteraction,
  };

  const autoCompleteFilterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (p: Product) => {
      return [p.symbol, p.product_group, p.full_name].join('_');
    },
  });

  return (
    <Box
      sx={{
        borderRadius: '4px',
        padding: '1em',
        display: 'flex',
        flexDirection: 'column',
        gap: '1em',
      }}
      data-testid={productEntryContainerId}
    >
      <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
        <FormControl {...DefaultFormControlProps} sx={{ minWidth: hidePrices ? '100%' : '70%' }}>
          <Autocomplete
            options={products}
            getOptionLabel={o => (typeof o === 'string' ? o : o.symbol)}
            value={null}
            inputValue={searchString || ''}
            onFocus={() => {
              registerSearchInteraction();
            }}
            renderOption={(props, option: Product, ignored_state, ignored_ownerState) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <span>{option.symbol.toUpperCase()}</span>
                  <Typography variant="body" sx={{ marginLeft: '1em' }}>
                    {option.full_name}
                  </Typography>
                </li>
              );
            }}
            selectOnFocus={true}
            clearOnEscape={true}
            clearOnBlur={true}
            handleHomeEndKeys={true}
            onInputChange={onAutocompleteInputChange}
            isOptionEqualToValue={(option: Product, value: Product) => {
              return option.symbol === value.symbol;
            }}
            renderInput={params => {
              return <TextField {...params} label="Search..." />;
            }}
            onClick={registerSearchInteraction}
            filterOptions={autoCompleteFilterOptions}
            autoComplete={true}
            autoSelect={true}
            autoHighlight={true}
            freeSolo={true}
            fullWidth={true}
            groupBy={p => capitalise(p.product_group, '_', ' ')}
            onChange={onSelectionChange}
          />
        </FormControl>
        {!hidePrices && <SimplePriceTicker product={product} tenor={tenor} frontTenor={frontTenor} backTenor={backTenor} />}
      </div>

      {/* Manual entry */}
      <div style={{ display: 'flex', flexWrap: 'nowrap', width: '100%', justifyContent: 'space-between', alignContent: 'center' }}>
        {/* Groups */}
        <form>
          <FormControl {...DefaultFormControlProps}>
            <InputLabel>Product Group</InputLabel>
            <Select {...DefaultSelectProps} label="Group" value={group || ''} onChange={handleGroupChange}>
              {productGroups.map(g => {
                return (
                  <MenuItem key={g} value={g}>
                    {capitalise(g, '_', ' ')}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Products */}

          <FormControl {...DefaultFormControlProps} disabled={group === null}>
            <InputLabel>Product</InputLabel>
            <Select {...DefaultSelectProps} label="Product" value={product?.symbol || ''} onChange={handleProductChange}>
              {availableProducts.map(p => {
                return (
                  <MenuItem key={p.symbol} value={p.symbol}>
                    {p.symbol.toUpperCase()}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Tenors */}

          <FormControl {...DefaultFormControlProps} disabled={product === null}>
            {/* TODO: is there a better way to label this? */}
            <InputLabel>{isSpread ? 'Spread Month' : 'Tenor'}</InputLabel>
            <Select label="Tenor" value={tenor?.code || ''} onChange={handleTenorChange}>
              {validTenors.map(t => {
                return (
                  <MenuItem key={t.code} value={t.code}>
                    {t.display.toUpperCase()}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Exchange */}

          {!hideExchange && (
            <FormControl {...DefaultFormControlProps} disabled={product === null}>
              <InputLabel>Exchange</InputLabel>
              <Select
                label="Exchange"
                value={exchange ? exchange : ''}
                onChange={e => {
                  setExchange(e.target.value);
                }}
              >
                {exchanges.map(e => {
                  return (
                    <MenuItem key={e} value={e}>
                      {e.toUpperCase()}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {/* Target price */}

          {!hideTargetPrice && (
            <FormControl {...DefaultFormControlProps}>
              <InputLabel>Target Price</InputLabel>
              <Input
                value={targetPrice || ''}
                inputMode="numeric"
                onChange={handleTargetPriceChange}
                onClick={registerFormInteraction}
                onFocus={e => {
                  registerFormInteraction();
                  e.target.select();
                }}
                sx={{ maxWidth: 110 }}
              />
            </FormControl>
          )}
        </form>
        <ButtonGroup sx={{ alignItems: 'flex-end', justifySelf: 'flex-end', placeSelf: 'flex-end' }}>
          <Button onClick={submit} disabled={actionButtonsDisabled} sx={actionButtonStyle}>
            {props.submitLabel || 'Go'}
          </Button>

          {(props.otherActions || []).map(action => {
            return (
              <Button
                onClick={() => {
                  submitAction(action);
                }}
                disabled={actionButtonsDisabled}
                sx={actionButtonStyle}
              >
                {action.label}
              </Button>
            );
          })}

          <Button onClick={cancel} sx={actionButtonStyle} color="error">
            Cancel
          </Button>
        </ButtonGroup>
      </div>
    </Box>
  );
};

export const ProductEntryContainer = (props: React.PropsWithChildren & { isOpen: boolean }) => {
  const { isOpen, children } = props;

  return (
    <div
      style={{
        width: '100%',
        height: 'calc(100% - 25px - 4rem)',
        top: '25px',
        left: 0,
        paddingTop: '2rem',
        display: 'flex',
        background: 'rgba(16, 16, 16, 0.6)',
        placeItems: 'center',
        placeContent: 'center space-around',
        position: 'absolute',
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? 'all' : 'none',
        transition: 'all 0.1s ease',
      }}
    >
      {/* Inner popup */}
      <div
        style={{
          width: 'calc(100% - 5em)',
          top: 'calc(25px + 2.5em)',
          left: '2.5em',
          backgroundColor: darkTheme.palette.background.lighter,
          transition: 'all 0.2s ease',
          zIndex: 3, // agGrid's 'no rows' overlay has a zIndex of 2
          borderRadius: '1em',
        }}
      >
        {children}
      </div>
    </div>
  );
};
